/**
 * Per-request context computed by proxy.ts (store, locale, split tests, preview, cached globals).
 *
 * The proxy is the only server-only code that runs before `_app.getInitialProps`, and `_app` is isomorphic,
 * so the context is handed over in-process: the proxy publishes it under a random id and tags the request
 * with that id; `_app`, `_document` and getServerSideProps look it up through this module, which touches
 * nothing but globalThis and is therefore safe in the client bundle (where it simply finds nothing).
 */
import type { IncomingMessage, ServerResponse } from 'http'
import castArray from 'lodash/castArray'
import type { GetServerSidePropsContext } from 'next'

import type { GlobalInjectionsQuery, GlobalNavigationQuery } from '@Generated/graphql'
import emptyStore from '@Globals/emptyStore'
import type { AvailableLocale, Bar, CMSData, FacetDescriptions, PaymentMethod, Store } from '@Globals/Metadata'
import { addSurrogateKey, addVary } from '@Globals/Page/Headers'
import type { SplitTests } from '@Globals/SplitTests/types'

export interface RequestContext {
  availableLocales: AvailableLocale[]
  bars: Bar[]
  cmsData: CMSData
  facetDescriptions: FacetDescriptions
  injections: GlobalInjectionsQuery['injections']
  locale: string
  navigation: GlobalNavigationQuery['navigation']
  navigationTop: GlobalNavigationQuery['navigationTop']
  /** Dev / ALLOW_OVERRIDE only: the URL the store is being served from instead of its canonical domain. */
  overrideStoreURLFull?: string
  paymentMethods: PaymentMethod[]
  preview: boolean
  previewOverrideCurrentDate?: string
  /** Set-Cookie value merging a `th_split_test` query parameter into the cookie; set by _app on the Node response so
   * the comma-separated value is not split the way the proxy's Set-Cookie header would be. */
  setSplitTestCookie?: string
  splitTests: SplitTests
  store: Store
  storeFound: boolean
}

export const emptyRequestContext = (): RequestContext => ({
  availableLocales: [],
  bars: [],
  cmsData: {
    latestCollections: [],
    urls: {
      aboutUs: null,
      articles: null,
      cookiePolicy: null,
      csr: null,
      faq: null,
      giftGuide: null,
      press: null,
      privacyPolicy: null,
      returns: null,
      terms: null,
    },
  },
  facetDescriptions: {},
  injections: [],
  locale: 'en-GB',
  navigation: [],
  navigationTop: [],
  paymentMethods: [],
  preview: false,
  splitTests: {},
  store: { ...emptyStore },
  storeFound: false,
})

export const REQUEST_ID_HEADER = 'x-th-request'

interface Slot {
  context: RequestContext
  createdAt: number
}

const registry = globalThis as typeof globalThis & { thRequestContexts?: Map<string, Slot> }
const slots = () => {
  registry.thRequestContexts ??= new Map()
  return registry.thRequestContexts
}

// Requests that never reach _app (aborted, or handled before rendering) would otherwise leak their slot.
const SLOT_TTL_MS = 60_000
const sweep = () => {
  const cutoff = Date.now() - SLOT_TTL_MS
  slots().forEach((slot, id) => {
    if (slot.createdAt < cutoff) {
      slots().delete(id)
    }
  })
}

export const publishRequestContext = (id: string, context: RequestContext): void => {
  sweep()
  slots().set(id, { context, createdAt: Date.now() })
}

const requestID = (req: IncomingMessage | undefined): string | undefined => {
  const value = req?.headers[REQUEST_ID_HEADER]
  return Array.isArray(value) ? value[0] : value
}

/** Undefined in the browser and for requests the proxy did not tag (static assets, API routes). */
export const getRequestContext = (req: IncomingMessage | undefined): RequestContext | undefined => {
  const id = requestID(req)
  return id ? slots().get(id)?.context : undefined
}

export const releaseRequestContext = (req: IncomingMessage | undefined): void => {
  const id = requestID(req)
  if (id) {
    slots().delete(id)
  }
}

export const getRequestContextFromGSSP = (context: Pick<GetServerSidePropsContext, 'req'>): RequestContext => (
  getRequestContext(context.req) || emptyRequestContext()
)

/**
 * Restores the `req.th*` / `res.th*` contract getServerSideProps and _document rely on. Called once per
 * request from _app.getInitialProps, which Next runs before any getServerSideProps.
 */
export const hydrateRequest = (req: IncomingMessage, res: ServerResponse, context: RequestContext): void => {
  req.thAvailableLocales = context.availableLocales
  req.thBars = context.bars
  req.thCMSData = context.cmsData
  req.thFacetDescriptions = context.facetDescriptions
  req.thInjections = context.injections
  req.thNavigation = context.navigation
  req.thNavigationTop = context.navigationTop
  req.thOverrideStoreURLFull = context.overrideStoreURLFull
  req.thPaymentMethods = context.paymentMethods
  req.thPreview = context.preview
  req.thPreviewOverrideCurrentDate = context.previewOverrideCurrentDate
  req.thSplitTests = context.splitTests
  req.thStore = context.store

  if (context.setSplitTestCookie) {
    const existing = res.getHeader('Set-Cookie')
    const cookies = existing === undefined ? [] : castArray(existing).map(String)
    res.setHeader('Set-Cookie', [...cookies, context.setSplitTestCookie])
  }

  res.thAddSurrogateKey = (key: string) => addSurrogateKey(res, key)
  res.thAddVary = (header: string) => addVary(res, header)
}
